import React, { lazy, Suspense, useEffect, useRef } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import Lenis from 'lenis';
import { LenisContext } from './LenisContext';
import { ThemeProvider } from './ThemeContext';
import { MouseProvider } from './MouseContext';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import IntroHeader from './components/IntroHeader';
import HomeAutomationHeader from './components/HomeAutomationHeader';
import Footer from './components/Footer';
import BackToTop from './components/BackToTop';
import WhatsAppCTA from './components/WhatsAppCTA';

// Page Imports (eager — critical paths)
import Home from './pages/Home';
import HomeAutomation from './pages/HomeAutomation';
import IndustrialAutomation from './pages/IndustrialAutomation';
import AboutUs from './pages/AboutUs';
import ContactUs from './pages/ContactUs';

gsap.registerPlugin(ScrollTrigger);



if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

// Page Imports (lazy — secondary paths)
const ExperienceCenter = lazy(() => import('./pages/ExperienceCenter'));
const ComfortExperience = lazy(() => import('./pages/ComfortExperience'));
const SecurityExperience = lazy(() => import('./pages/SecurityExperience'));
const CaseStudies = lazy(() => import('./pages/CaseStudies'));
const IndustriesPage = lazy(() => import('./pages/IndustriesPage'));
const Services = lazy(() => import('./pages/Services'));
const Blog = lazy(() => import('./pages/Blog'));
const Features = lazy(() => import('./pages/Features'));
const Products = lazy(() => import('./pages/Products'));
const Downloads = lazy(() => import('./pages/Downloads'));
const FAQs = lazy(() => import('./pages/FAQs'));
const SolutionsPage = lazy(() => import('./pages/Solutions'));
const SolutionOffice = lazy(() => import('./pages/solutions/SolutionOffice'));
const SolutionBuilding = lazy(() => import('./pages/solutions/SolutionBuilding'));
const SolutionHotel = lazy(() => import('./pages/solutions/SolutionHotel'));
const SolutionHospital = lazy(() => import('./pages/solutions/SolutionHospital'));
const SolutionHostel = lazy(() => import('./pages/solutions/SolutionHostel'));
const Walkthrough3D = lazy(() => import('./pages/Walkthrough3D'));

// ── Page Transition Wrapper ──────────────────────────────────
// Now utilizing a 3D depth-zoom morph instead of a flat vertical slide
const PageTransition = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.96, filter: 'blur(8px)' }}
    animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
    exit={{ opacity: 0, scale: 1.04, filter: 'blur(8px)' }}
    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
    className="origin-center min-h-screen"
  >
    {children}
  </motion.div>
);

// ── Loading Fallback ─────────────────────────────────────────
function PageLoader() {
  return (
    <div className="min-h-screen bg-theme flex items-center justify-center">
      <div className="w-6 h-6 rounded-full border-2 border-gold/30 border-t-gold animate-spin" />
    </div>
  );
}

// ── Grain Overlay ─────────────────────────────────────────────
function GrainOverlay() {
  return <div className="grain-layer" aria-hidden="true" />;
}

// ── Light Vignette ──────────────────────────────────────────
function LightVignette() {
  return (
    <div 
      className="fixed inset-0 pointer-events-none z-[50]" 
      style={{
        background: 'radial-gradient(circle at 50% 50%, transparent 0%, rgba(45, 42, 38, 0.02) 100%)',
      }}
      aria-hidden="true"
    />
  );
}

// ── App ───────────────────────────────────────────────────────
function App() {
  const location = useLocation();
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 2.0,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 1.1,
      gestureOrientation: 'vertical',
      normalizeWheel: true,
    });
    lenisRef.current = lenis;

    // Sync Lenis scroll with GSAP ScrollTrigger
    lenis.on('scroll', ScrollTrigger.update);

    window.scrollTo(0, 0);
    lenis.scrollTo(0, { immediate: true });

    gsap.ticker.add((time) => {
      lenis.raf(time * 1000);
    });
    gsap.ticker.lagSmoothing(0);

    return () => {
      lenis.destroy();
      gsap.ticker.remove(lenis.raf);
    };
  }, []);

  const isImmersive = location.pathname === '/home-automation/walkthrough' || 
                      location.pathname === '/home-automation/security-experience';

  useEffect(() => {
    if (lenisRef.current) {
      lenisRef.current.scrollTo(0, { immediate: true });
      
      // Stop Lenis on immersive pages to allow native internal scroll
      if (isImmersive) {
        lenisRef.current.stop();
      } else {
        lenisRef.current.start();
      }
    }
  }, [location.pathname, isImmersive]);

  return (
    <ThemeProvider>
      <MouseProvider>
        <LenisContext.Provider value={lenisRef}>
          {/* IntroHeader: only on the landing / page */}
          {location.pathname === '/' && <IntroHeader />}

          {/* HomeAutomationHeader: all other non-immersive pages */}
          {location.pathname !== '/' && (location.pathname === '/home-automation/comfort-experience' || !isImmersive) && <HomeAutomationHeader />}
          <div className="bg-theme text-theme relative min-h-screen">
            <GrainOverlay />
            <LightVignette />
            {location.pathname !== '/' && <BackToTop />}
            {!isImmersive && location.pathname !== '/' && <WhatsAppCTA />}


          <Suspense fallback={<PageLoader />}>
            <AnimatePresence mode="wait" initial={false}>
              <Routes location={location} key={location.pathname}>
                {/* ── HOMEPAGE ── */}
                <Route path="/" element={<PageTransition><Home /></PageTransition>} />

                {/* ── B2C FLOW ── */}
                <Route path="/home-automation" element={<PageTransition><HomeAutomation /></PageTransition>} />
                <Route path="/home-automation/comfort-experience" element={<PageTransition><ComfortExperience /></PageTransition>} />
                <Route path="/home-automation/security-experience" element={<SecurityExperience />} />
                <Route path="/home-automation/walkthrough" element={<Suspense fallback={<PageLoader />}><Walkthrough3D /></Suspense>} />

                {/* ── B2B FLOW ── */}
                <Route path="/industrial-automation" element={<PageTransition><IndustrialAutomation /></PageTransition>} />
                <Route path="/industrial-automation/case-studies" element={<PageTransition><CaseStudies /></PageTransition>} />
                <Route path="/industrial-automation/industries" element={<PageTransition><IndustriesPage /></PageTransition>} />

                {/* ── SHARED CORE ── */}
                <Route path="/about" element={<PageTransition><AboutUs /></PageTransition>} />
                <Route path="/services" element={<PageTransition><Services /></PageTransition>} />
                <Route path="/blog" element={<PageTransition><Blog /></PageTransition>} />
                <Route path="/contact" element={<PageTransition><ContactUs /></PageTransition>} />

                {/* ── MISC ── */}
                <Route path="/features" element={<PageTransition><Features /></PageTransition>} />
                <Route path="/products" element={<PageTransition><Products /></PageTransition>} />
                <Route path="/downloads" element={<PageTransition><Downloads /></PageTransition>} />
                <Route path="/faqs" element={<PageTransition><FAQs /></PageTransition>} />
                <Route path="/solutions" element={<PageTransition><SolutionsPage /></PageTransition>} />
                <Route path="/solutions/office" element={<PageTransition><SolutionOffice /></PageTransition>} />
                <Route path="/solutions/building" element={<PageTransition><SolutionBuilding /></PageTransition>} />
                <Route path="/solutions/hotel" element={<PageTransition><SolutionHotel /></PageTransition>} />
                <Route path="/solutions/hospital" element={<PageTransition><SolutionHospital /></PageTransition>} />
                <Route path="/solutions/hostel" element={<PageTransition><SolutionHostel /></PageTransition>} />
              </Routes>
            </AnimatePresence>
          </Suspense>

          {!isImmersive && location.pathname !== '/' && <Footer />}
        </div>
        </LenisContext.Provider>
      </MouseProvider>
    </ThemeProvider>
  );
}

export default App;
